import { useCallback, useContext, useEffect, useState } from 'react'
import api from '../../api'
import { ListContext, ProjectContext } from '../../contexts'
import { DynamicList, ListUpdateParams, Rule, Subscription } from '../../types'
import Button from '../../ui/Button'
import Heading from '../../ui/Heading'
import PageContent from '../../ui/PageContent'
import RuleBuilder from './rules/RuleBuilder'
import { RuleErrorsContext, ruleTemplateErrorMessage, useRuleErrors } from './rules/RuleHelpers'
import Modal from '../../ui/Modal'
import FormWrapper from '../../ui/form/FormWrapper'
import TextInput from '../../ui/form/TextInput'
import { ListTag } from './ListTable'
import { InfoTable } from '../../ui/InfoTable'
import { checkProjectRole, snakeToTitle } from '../../utils'
import UserUploadModal from '../users/UserUploadModal'
import { Table, useSearchTableState } from '../../components'
import { useRoute } from '../router'
import { ArchiveIcon, CheckCircleIcon, DownloadIcon, EditIcon, SearchIcon, TrashIcon, UploadIcon } from '../../ui/icons'
import { TagPicker } from '../settings/TagPicker'
import { ModifiedTag } from '../campaign/ui/ModifiedTag'
import { useTranslation } from 'react-i18next'
import { Alert, Menu, MenuItem } from '../../ui'
import { useBlocker } from 'react-router'
import { useDebounceControl } from '../../hooks'
import { trackExport } from '../exports/exportTracker'
import { toast } from 'react-hot-toast/headless'
import { SingleSelect } from '../../ui/form/SingleSelect'
import SwitchField from '../../ui/form/SwitchField'

interface RuleSectionProps {
    list: DynamicList
    isSaving: boolean
    onRuleSave: (rule: Rule) => void
    onChange?: (rule: Rule) => void
}

const RuleSection = ({ list, isSaving, onRuleSave, onChange }: RuleSectionProps) => {
    const { t } = useTranslation()
    const [rule, setRule] = useState<Rule>(list.rule)
    const onSetRule = (rule: Rule) => {
        setRule(rule)
        onChange?.(rule)
    }
    return <>
        <Heading size="h3" title={t('rules')} actions={
            <Button
                size="small"
                onClick={() => onRuleSave(rule) }
                isLoading={isSaving}
            >{t('rules_save')}</Button>
        } />
        <RuleBuilder rule={rule} setRule={onSetRule} listConditions />
    </>
}

export default function ListDetail() {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [list, setList] = useContext(ListContext)
    const [isEditListOpen, setIsEditListOpen] = useState(false)
    const [isUploadOpen, setIsUploadOpen] = useState(false)
    const [isDeleteUsersOpen, setIsDeleteUsersOpen] = useState(false)
    const [isExportOpen, setIsExportOpen] = useState(false)
    const [isSubscribeOpen, setIsSubscribeOpen] = useState(false)
    const [topics, setTopics] = useState<Subscription[]>([])
    const [topicId, setTopicId] = useState<number | undefined>()
    const [triggerJourneys, setTriggerJourneys] = useState(false)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const ruleErrors = useRuleErrors()

    const state = useSearchTableState(useCallback(async params => await api.lists.users(project.id, list.id, params), [list, project]))
    const route = useRoute()
    const [userSearch, setUserSearch] = useDebounceControl(state.params.q ?? '', q => state.setParams({ ...state.params, q, cursor: undefined }))

    const refreshList = () => {
        api.lists.get(project.id, list.id)
            .then(setList)
            .then(() => state.reload)
            .catch(() => {})
    }

    useEffect(() => {
        if (list.state !== 'loading') return
        const complete = list.progress?.complete ?? 0
        const total = list.progress?.total ?? 0
        const percent = total > 0 ? complete / total * 100 : 0
        const refreshRate = percent < 5 ? 1000 : 5000
        const interval = setInterval(refreshList, refreshRate)
        refreshList()

        return () => clearInterval(interval)
    }, [list.state])

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
    )

    useEffect(() => {
        if (blocker.state !== 'blocked') return
        if (confirm(t('confirm_unsaved_changes'))) {
            blocker.proceed()
        } else {
            blocker.reset()
        }
    }, [blocker.state])

    const saveList = async ({ name, description, rule, tags }: ListUpdateParams) => {
        setIsSaving(true)
        try {
            const value = await api.lists.update(project.id, list.id, { name, description, rule, tags })
            setError(undefined)
            setList(value)
            setIsEditListOpen(false)
            setHasUnsavedChanges(false)
        } catch (error: any) {
            const details = ruleErrors.setFromResponse(error)
            setError(details ? ruleTemplateErrorMessage(t, details) : error.response?.data?.error ?? error.message)
            setIsEditListOpen(false)
        } finally {
            setIsSaving(false)
        }
    }

    const handleArchiveList = async () => {
        await api.lists.delete(project.id, list.id)
        window.location.href = `/projects/${project.id}/lists`
    }

    const handleDeleteUsers = async () => {
        try {
            await api.lists.deleteUsers(project.id, list.id)
            setIsDeleteUsersOpen(false)
            refreshList()
        } catch (error: any) {
            const errorMessage = error.response?.data?.error ?? error.message
            setError(errorMessage)
            setIsDeleteUsersOpen(false)
        }
    }

    const openSubscribe = () => {
        setTopicId(undefined)
        setTriggerJourneys(false)
        setIsSubscribeOpen(true)
        api.subscriptions.search(project.id, { limit: 9999, q: '' })
            .then(({ results }) => setTopics(results.filter(s => !s.is_channel)))
            .catch(() => {})
    }

    const handleSubscribeUsers = async () => {
        if (!topicId) return
        try {
            await api.lists.subscribeUsers(project.id, list.id, { subscription_id: topicId, trigger_journeys: triggerJourneys })
            setIsSubscribeOpen(false)
            toast.success(t('list_subscribe_users_started'))
        } catch (error: any) {
            const errorMessage = error.response?.data?.error ?? error.message
            setError(errorMessage)
            setIsSubscribeOpen(false)
        }
    }

    const handleExport = async () => {
        try {
            const record = await api.lists.export(project.id, list.id)
            setIsExportOpen(false)
            trackExport(project.id, record)
        } catch (error: any) {
            const errorMessage = error.response?.data?.error ?? error.message
            setError(errorMessage)
            setIsExportOpen(false)
        }
    }

    const isAdmin = checkProjectRole('admin', project.role)

    return (
        <PageContent
            title={list.name}
            desc={
                <>
                    <InfoTable rows={{
                        [t('state')]: <ListTag state={list.state} progress={list.progress} />,
                        [t('type')]: snakeToTitle(list.type),
                        [t('users_count')]: list.state === 'loading'
                            ? <>&#8211;</>
                            : list.users_count?.toLocaleString(),
                        ...list.source === 'mixpanel' && list.source_config?.cohort_name && {
                            [t('source')]: `Mixpanel · ${list.source_config.cohort_name}`,
                        },
                        ...list.updated_by && { [t('updated_at')]: <ModifiedTag admin={list.updated_by} /> },
                    }} direction="horizontal" />
                    {list.description && <p style={{ whiteSpace: 'pre-wrap' }}>{list.description}</p>}
                </>
            }
            actions={
                <>
                    {list.type === 'static' && <Button
                        variant="secondary"
                        icon={<UploadIcon />}
                        onClick={() => setIsUploadOpen(true)}
                    >{t('upload_list')}</Button>}
                    <Button icon={<EditIcon />} onClick={() => setIsEditListOpen(true)}>{t('edit_list')}</Button>
                    <Menu size="regular">
                        <MenuItem icon={<ArchiveIcon />} onClick={async () => await handleArchiveList()}>
                            {t('archive')}
                        </MenuItem>
                        <MenuItem icon={<DownloadIcon />} onClick={() => setIsExportOpen(true)}>
                            {t('export_users')}
                        </MenuItem>
                        {isAdmin && <MenuItem icon={<CheckCircleIcon />} onClick={openSubscribe}>
                            {t('list_subscribe_users')}
                        </MenuItem>}
                        {isAdmin && <MenuItem icon={<TrashIcon />} onClick={() => setIsDeleteUsersOpen(true)}>
                            {t('delete_list_users')}
                        </MenuItem>}
                    </Menu>
                </>
            }>

            {error && <Alert variant="error" title={t('error')}>{error}</Alert>}

            {list.type === 'dynamic' && (
                <RuleErrorsContext.Provider value={ruleErrors.value}>
                    <RuleSection
                        list={list}
                        isSaving={isSaving}
                        onRuleSave={async (rule: any) => await saveList({ name: list.name, rule })}
                        onChange={() => setHasUnsavedChanges(true)} />
                </RuleErrorsContext.Provider>
            )}

            <Table title={t('users')}
                {...state}
                actions={
                    <TextInput
                        name="user_search"
                        value={userSearch}
                        onChange={setUserSearch}
                        placeholder={t('list_user_search_placeholder')}
                        hideLabel
                        icon={<SearchIcon />}
                    />
                }
                columns={[
                    { key: 'full_name', title: t('name') },
                    { key: 'external_id', title: t('external_id') },
                    { key: 'email', title: t('email') },
                    { key: 'phone', title: t('phone') },
                ]}
                onSelectRow={({ id }) => route(`users/${id}`)} />

            <Modal
                open={isEditListOpen}
                onClose={() => setIsEditListOpen(false)}
                title={t('edit_list')}>
                <FormWrapper<Omit<ListUpdateParams, 'rule'>>
                    onSubmit={async ({ name, description, tags }) => await saveList({ name, description, tags })}
                    submitLabel={t('save')}
                    defaultValues={{ name: list.name, description: list.description, tags: list.tags }}
                >
                    {form => (
                        <>
                            <TextInput.Field
                                form={form}
                                name="name"
                                label={t('list_name')}
                                required
                            />
                            <TextInput.Field
                                form={form}
                                name="description"
                                label={t('description')}
                                textarea
                                autosize
                            />
                            <TagPicker.Field
                                form={form}
                                name="tags"
                                label={t('tags')}
                            />
                        </>
                    )}
                </FormWrapper>
            </Modal>

            <UserUploadModal
                open={isUploadOpen}
                onClose={(success) => {
                    setIsUploadOpen(false)
                    if (success) refreshList()
                }}
                listId={list.id}
            />

            <Modal
                open={isDeleteUsersOpen}
                onClose={() => setIsDeleteUsersOpen(false)}
                title={t('delete_list_users')}
                actions={
                    <>
                        <Button variant="secondary" onClick={() => setIsDeleteUsersOpen(false)}>{t('cancel')}</Button>
                        <Button variant="destructive" onClick={handleDeleteUsers}>{t('delete_list_users_confirm')}</Button>
                    </>
                }
            >
                <p>{t('delete_list_users_description', { count: list.users_count ?? 0 })}</p>
                <p><strong>{t('delete_list_users_warning')}</strong></p>
            </Modal>

            <Modal
                open={isSubscribeOpen}
                onClose={() => setIsSubscribeOpen(false)}
                title={t('list_subscribe_users')}
                actions={
                    <>
                        <Button variant="secondary" onClick={() => setIsSubscribeOpen(false)}>{t('cancel')}</Button>
                        <Button onClick={handleSubscribeUsers} disabled={!topicId || list.state === 'loading'}>
                            {t('list_subscribe_users_confirm', { total: (list.users_count ?? 0).toLocaleString() })}
                        </Button>
                    </>
                }
            >
                <p>{t('list_subscribe_users_description')}</p>
                <SingleSelect
                    label={t('list_subscribe_users_topic')}
                    options={topics}
                    value={topicId}
                    onChange={setTopicId}
                    toValue={x => x.id}
                    getOptionSubtitle={x => t(x.channel)}
                    searchable
                    required
                />
                <SwitchField
                    name="trigger_journeys"
                    label={t('list_subscribe_users_journeys')}
                    checked={triggerJourneys}
                    onChange={setTriggerJourneys}
                />
                <p>{t('list_subscribe_users_skip_note')}</p>
            </Modal>

            <Modal
                open={isExportOpen}
                onClose={() => setIsExportOpen(false)}
                title={t('export_users')}
                actions={
                    <>
                        <Button variant="secondary" onClick={() => setIsExportOpen(false)}>{t('cancel')}</Button>
                        <Button onClick={handleExport}>{t('export_users_confirm')}</Button>
                    </>
                }
            >
                <p>{t('export_list_users_description', { count: list.users_count ?? 0 })}</p>
                <p>{t('export_snapshot_note')}</p>
            </Modal>
        </PageContent>
    )
}
